import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import { formatarData } from '@/lib/formato';
import { BarraFiltros, type FiltroAtivo } from '@/ui/components/barra-filtros';
import { Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';

import { type FiltrosDocumentos } from './api';
import { type ChaveFiltro } from './filtros';
import { CLASSIFICACAO_NOTA, DIRECAO, MODELO, SITUACAO, STATUS_CLASSIFICACAO } from './rotulos';

interface Props {
  filtros: FiltrosDocumentos;
  definir: (chave: ChaveFiltro, valor: string | undefined) => void;
  limpar: () => void;
}

const opcoes = <T extends string>(mapa: Record<T, string | { rotulo: string }>) =>
  (Object.entries(mapa) as [T, string | { rotulo: string }][]).map(([valor, r]) => ({
    valor,
    rotulo: typeof r === 'string' ? r : r.rotulo,
  }));

/** Os filtros de lista fixa, na ordem da barra. "Conferência" é o andamento; "Classificação", o destino. */
const SELECTS: readonly {
  chave: 'direcao' | 'modelo' | 'situacao' | 'statusClassificacao' | 'classificacao';
  rotulo: string;
  largura: string;
  opcoes: ReturnType<typeof opcoes>;
}[] = [
  { chave: 'direcao', rotulo: 'Direção', largura: 'w-36', opcoes: opcoes(DIRECAO) },
  { chave: 'modelo', rotulo: 'Modelo', largura: 'w-32', opcoes: opcoes(MODELO) },
  { chave: 'situacao', rotulo: 'Situação', largura: 'w-40', opcoes: opcoes(SITUACAO) },
  {
    chave: 'statusClassificacao',
    rotulo: 'Conferência',
    largura: 'w-40',
    opcoes: opcoes(STATUS_CLASSIFICACAO),
  },
  {
    chave: 'classificacao',
    rotulo: 'Classificação',
    largura: 'w-48',
    opcoes: opcoes(CLASSIFICACAO_NOTA),
  },
];

function ativos(filtros: FiltrosDocumentos, definir: Props['definir']): FiltroAtivo[] {
  const lista: FiltroAtivo[] = [];
  const add = (chave: ChaveFiltro, rotulo: string) => {
    lista.push({
      id: chave,
      rotulo,
      remover: () => {
        definir(chave, undefined);
      },
    });
  };
  if (filtros.direcao) add('direcao', DIRECAO[filtros.direcao]);
  if (filtros.modelo) add('modelo', MODELO[filtros.modelo]);
  if (filtros.situacao) add('situacao', SITUACAO[filtros.situacao].rotulo);
  if (filtros.statusClassificacao) {
    add('statusClassificacao', STATUS_CLASSIFICACAO[filtros.statusClassificacao].rotulo);
  }
  if (filtros.classificacao) add('classificacao', CLASSIFICACAO_NOTA[filtros.classificacao]);
  if (filtros.emitidaDe) add('emitidaDe', `Desde ${formatarData(filtros.emitidaDe)}`);
  if (filtros.emitidaAte) add('emitidaAte', `Até ${formatarData(filtros.emitidaAte)}`);
  if (filtros.q) add('q', `“${filtros.q}”`);
  return lista;
}

export function BarraFiltrosDocumentos({ filtros, definir, limpar }: Props) {
  const [busca, setBusca] = useState(filtros.q ?? '');
  // Filtro tirado pelo chip (ou pela URL): a caixa de busca acompanha, sem efeito em cascata.
  const [qAnterior, setQAnterior] = useState(filtros.q);
  if (filtros.q !== qAnterior) {
    setQAnterior(filtros.q);
    setBusca(filtros.q ?? '');
  }
  useEffect(() => {
    const t = setTimeout(() => {
      if (busca.trim() !== (filtros.q ?? '')) definir('q', busca.trim() || undefined);
    }, 300);
    return () => {
      clearTimeout(t);
    };
  }, [busca, filtros.q, definir]);

  return (
    <BarraFiltros ativos={ativos(filtros, definir)} limparTudo={limpar}>
      <label className="relative min-w-64 flex-1">
        <span className="sr-only">Buscar notas</span>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-texto-fraco"
          aria-hidden
        />
        <Input
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
          }}
          placeholder="Chave, número, emitente ou CNPJ"
          className="pl-9"
        />
      </label>
      {SELECTS.map((f) => (
        <Select
          key={f.chave}
          aria-label={f.rotulo}
          className={f.largura}
          placeholder={f.rotulo}
          opcoes={f.opcoes}
          valor={filtros[f.chave] ?? ''}
          aoMudar={(v) => {
            definir(f.chave, v);
          }}
        />
      ))}
      <Input
        type="date"
        aria-label="Emitidas desde"
        className="w-40"
        value={filtros.emitidaDe ?? ''}
        onChange={(e) => {
          definir('emitidaDe', e.target.value || undefined);
        }}
      />
      <Input
        type="date"
        aria-label="Emitidas até"
        className="w-40"
        value={filtros.emitidaAte ?? ''}
        onChange={(e) => {
          definir('emitidaAte', e.target.value || undefined);
        }}
      />
    </BarraFiltros>
  );
}
